import React from "react";
import PropTypes from "prop-types";
import { cn } from "../cn";
import { Button } from "../Button";
import styles from "./TablePanel.module.scss";

const DEFAULT_SIZES = [5, 10, 25, 50];

function PagerControls({
  page,
  totalPages,
  onPageChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  idPrefix = "pager",
}) {
  return (
    <div className={styles.controls}>
      {typeof onPageSizeChange === "function" ? (
        <label className={styles.pageSize}>
          Rows
          <select
            id={`${idPrefix}-page-size`}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Previous
      </Button>
      <span className={styles.pageLabel}>
        Page {page} of {totalPages}
      </span>
      <Button
        variant="secondary"
        size="sm"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </Button>
    </div>
  );
}

PagerControls.propTypes = {
  page: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  onPageSizeChange: PropTypes.func,
  idPrefix: PropTypes.string,
};

/**
 * Table chrome with row-count + pagination in BOTH header and footer.
 * Wrap a DS <Table> (or list stack) as children.
 */
export function TablePanel({
  title = null,
  total = 0,
  from = 0,
  to = 0,
  page = 1,
  totalPages = 1,
  onPageChange,
  pageSize = 10,
  pageSizeOptions = DEFAULT_SIZES,
  onPageSizeChange = null,
  className,
  children,
  empty = null,
}) {
  const showPager = total > 0 && typeof onPageChange === "function";
  const rangeLabel =
    total === 0 ? "0 rows" : `Showing ${from}–${to} of ${total}`;

  const pagerProps = {
    page,
    totalPages,
    onPageChange,
    pageSize,
    pageSizeOptions,
    onPageSizeChange,
  };

  return (
    <div className={cn(styles.panel, className)}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.title}>{title || "Results"}</div>
          <div className={styles.count}>{rangeLabel}</div>
        </div>
        {showPager ? (
          <div className={styles.headerPager}>
            <div className={styles.meta}>
              {total} {total === 1 ? "row" : "rows"}
            </div>
            <PagerControls {...pagerProps} idPrefix="header" />
          </div>
        ) : null}
      </div>

      <div className={styles.body}>
        {total === 0 && empty ? (
          <div className={styles.empty}>{empty}</div>
        ) : (
          children
        )}
      </div>

      {showPager ? (
        <div className={styles.footer}>
          <div className={styles.meta}>
            {total} {total === 1 ? "row" : "rows"}
          </div>
          <PagerControls {...pagerProps} idPrefix="footer" />
        </div>
      ) : null}
    </div>
  );
}

TablePanel.propTypes = {
  title: PropTypes.node,
  total: PropTypes.number,
  from: PropTypes.number,
  to: PropTypes.number,
  page: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  onPageSizeChange: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
  empty: PropTypes.node,
};

export default TablePanel;
